/**
 * Terms-of-Service clickwrap acceptance — shared constants.
 *
 * Single source of truth for the ToS version, legal-document URLs, and the
 * clickwrap sentence, shared by the dashboard gate (what the user sees) and
 * the API write-path (what gets stamped on the TosAcceptance row) so they can
 * never drift apart.
 *
 * Distinct from auto-renewal.ts: that file is the AB 2863 §17602 billing
 * consent (per-checkout); this is the general account-level ToS clickwrap
 * (per-user, per-version).
 */

/**
 * Bump this whenever a new ToS version is published to servegrace.com/terms.
 * Every user is re-gated by the blocking clickwrap modal until they accept
 * the current version; each acceptance writes a new TosAcceptance row.
 *
 * Format: the ToS effective date, YYYY-MM-DD.
 */
export const TOS_VERSION = '2026-06-30';

/** Public URLs of the live legal documents (WordPress). */
export const LEGAL_URLS = {
  terms: 'https://servegrace.com/terms/',
  privacy: 'https://servegrace.com/privacy/',
  dpa: 'https://servegrace.com/data-processing-addendum/',
  aup: 'https://servegrace.com/acceptable-use-policy/',
  legalHub: 'https://servegrace.com/legal/',
} as const;

/**
 * Retention floor for acceptance evidence, in years. Covers the California
 * 4-year written-contract statute of limitations (CCP §337) with margin.
 */
export const TOS_ACCEPTANCE_RETENTION_YEARS = 5;

/**
 * Retention floor for SMS/WhatsApp consent + opt-out evidence
 * (MessagingConsentEvent), in years. Covers the 4-year federal TCPA
 * limitations period (28 U.S.C. §1658) with margin.
 */
export const MESSAGING_CONSENT_RETENTION_YEARS = 5;

/**
 * The clickwrap sentence. The dashboard renders it next to an
 * unchecked-by-default checkbox (with the document names hyperlinked); the
 * API stores this exact string as the acceptance_text snapshot.
 */
export function buildTosClickwrapText(version: string = TOS_VERSION): string {
  return (
    `I have read and agree to the ServeGrace Terms of Service ` +
    `(effective ${version}) and acknowledge the Privacy Policy.`
  );
}
